import { FileText, Shield, Globe, Award, Users, BookOpen, CheckCircle, Star, Briefcase, GraduationCap } from 'lucide-react';

export const trainingModules = [
  {
    id: 'credential-transfer-portal',
    title: 'Credential Transfer Portal (CTP)',
    description: 'Credential Transfer Portal (CTP) DEMO',
    duration: 30,
    difficulty: 'Beginner',
    category: 'Documentation Services',
    tags: [],
    icon: <BookOpen size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: [],
      designations: [],
      specializations: []
    },
    content: {
      sections: [
        {
          title: 'CTP TRAINING',
          content: 'Credential Transfer Portal (CTP) – Business Summary\nThe Credential Transfer Portal (CTP) is an online system that helps educational institutions and licensing authorities send important documents—like academic records and license verifications—directly and securely to TruMerit. This replaces the need to mail paper documents, making the process faster and more reliable.\n\nKey Points:\nSecure & Efficient: The portal is a safer and quicker way to send documents compared to traditional mail.\n\nRestricted Access: Only officially recognized institutions (with .edu, .ac, .org, or .gov email domains) can use the portal.\n\nApproval Needed: Institutions must get approval from a high-level official (such as a Dean or Registrar) before using the portal.\n\nCurrently for Nursing: At this time, only nursing professionals can use the portal, but it may expand to other healthcare fields in the future.\n\nHow to Use: Institutions must apply for access. Once approved, authorized users can log in and submit documents online.\n\nAdditional Resources: There is a training video available for users, and the latest list of approved institutions and authorities is updated regularly.\n\nUseful Links:\nCredential Transfer Portal\n\nCTP Training Video',
          video: {
            src: 'https://meritiq.trumerit.org/videos/developer/CTP_Demo.mp4',
            title: 'CTP Demo Video',
            caption: 'CTP Demo Video'
          }
        }
      ]
    },
    quiz: [
      {
        question: 'What is the primary purpose of the Credential Transfer Portal (CTP)?',
        options: [
          'To replace traditional mail with a faster and more secure online document submission system',
          'To provide training videos for healthcare professionals',
          'To manage employee credentials internally',
          'To process payment transactions'
        ],
        correct: 0
      },
      {
        question: 'Which email domains are allowed to use the CTP portal?',
        options: [
          'Only .com domains',
          '.edu, .ac, .org, or .gov email domains',
          'Any email domain',
          'Only .gov domains'
        ],
        correct: 1
      },
      {
        question: 'Who must approve an institution before it can use the CTP portal?',
        options: [
          'Any staff member',
          'A high-level official such as a Dean or Registrar',
          'The IT department',
          'External auditors'
        ],
        correct: 1
      },
      {
        question: 'Which healthcare profession can currently use the CTP portal?',
        options: [
          'All healthcare professions',
          'Only nursing professionals',
          'Only physicians',
          'Only pharmacists'
        ],
        correct: 1
      },
      {
        question: 'What types of documents can be sent through the CTP portal?',
        options: [
          'Only academic records',
          'Only license verifications',
          'Academic records and license verifications',
          'Payment receipts only'
        ],
        correct: 2
      }
    ]
  },
  {
    id: 'credentials-verification-service',
    title: 'Credentials Verification Service (CVS)',
    description: 'Comprehensive training on CVS services for New York, New Zealand, and Aruba healthcare professional verification.',
    duration: 90,
    difficulty: 'Intermediate',
    category: 'Verification Services',
    tags: [],
    icon: <Shield size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'CVS New York Overview',
          content: 'The Credentials Verification Service for New York State®:\n\nPurpose:\n- Used by healthcare professionals educated outside the United States\n- Required for licensure from the New York State Education Department\n- Verifies and authenticates foreign academic and professional credentials\n\nProcess:\n- TruMerit verifies and authenticates applicants\' credentials\n- Delivers verification report to NYSED\n- NYSED evaluates credentials for licensure\n\nTarget Audience:\n- Individual nurses and healthcare professionals\n- Recruiters and employers\n- Fee: $425'
        },
        {
          title: 'CVS New Zealand (NCNZ)',
          content: 'Credentials Verification Service for Nursing Council of New Zealand:\n\nPurpose:\n- For internationally qualified nurses wishing to practice in New Zealand\n- Secure and direct method for credential verification\n- Excludes nurses licensed in Australia\n\nProcess:\n- Credentials verification and authentication\n- Direct submission to NCNZ\n- Supports licensing decisions\n\nTarget Audience:\n- Internationally qualified nurses (except Australia)\n- Fee: $380'
        },
        {
          title: 'CVS Aruba',
          content: 'Credentials Verification Service for Aruba:\n\nPurpose:\n- For health professionals educated outside Aruba\n- Required for registration to practice in Aruba\n- Supports Department of Public Health Aruba applications\n\nProcess:\n- Verifies and authenticates ID documents\n- Validates academic and professional credentials\n- Self-attestation of employment experience\n- Prepares report for Department of Public Health Aruba\n\nTarget Audience:\n- General Nurses, Nurse Specialists, Nurse Practitioners\n- Physician Assistants, Psychologists, Psychotherapists\n- Clinical Physicists\n- Fee: $395'
        },
        {
          title: 'Document Requirements and Process',
          content: 'Standard document requirements for CVS services:\n\nIdentity Documents:\n- Identity document collection (varies by service)\n- Identity document notarization (CVS-NCNZ, CVS-Aruba)\n- Passport style photo (VisaScreen)\n\nEducational Credentials:\n- Secondary school records (where applicable)\n- Unlimited diploma/degree/parchment documents\n- Course-by-course breakdown (CES Academic)\n\nProfessional Credentials:\n- Unlimited license/registration documents\n- Employment/work experience verification\n- Professional certification records\n\nAdditional Services:\n- Translation services ($85/page)\n- Expedited processing (where available)\n- Additional report recipients'
        }
      ]
    },
    quiz: [
      {
        question: 'What is the fee for CVS New York service?',
        options: ['$380', '$395', '$425', '$485'],
        correct: 2
      },
      {
        question: 'Which CVS service excludes nurses licensed in Australia?',
        options: ['CVS New York', 'CVS NCNZ', 'CVS Aruba', 'All CVS services'],
        correct: 1
      },
      {
        question: 'What is required for CVS Aruba applicants?',
        options: ['ID verification only', 'Self-attestation of employment', 'English language test', 'NCLEX exam'],
        correct: 1
      }
    ]
  },
  {
    id: 'credentials-evaluation-service',
    title: 'Credentials Evaluation Service (CES)',
    description: 'Training on CES Professional and Academic reports for nursing credential evaluation and comparability assessment.',
    duration: 75,
    difficulty: 'Intermediate',
    category: 'Credential Services',
    tags: [],
    icon: <FileText size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'CES Professional Report',
          content: 'Credentials Evaluation Service Professional Report:\n\nPurpose:\n- Detailed analysis of nursing credentials earned outside the United States\n- Primarily for licensing bodies\n- Used for licensure and employment purposes\n\nComponents:\n- Validates and authenticates all licenses and registrations\n- Verifies diplomas obtained by applicants\n- Statement of comparability against U.S. nursing standards\n- Educational program information\n\nTarget Audience:\n- Primarily licensing bodies\n- Regulators, employers, schools\n- Immigration attorneys and certification bodies\n- Fee: $485'
        },
        {
          title: 'CES Academic Report',
          content: 'Credentials Evaluation Service Academic Report:\n\nPurpose:\n- Comprehensive course-by-course analysis of nursing education\n- Advisory in nature\n- Used for academic placement\n\nComponents:\n- Course-by-course breakdown of education\n- GPA conversion (where applicable)\n- Educational program information\n- Statement of educational comparability\n\nTarget Audience:\n- International students seeking U.S. education\n- U.S. nurses seeking further education\n- Colleges and universities\n- Fee: $485'
        },
        {
          title: 'Specialty CES Services',
          content: 'Specialized CES services for nursing specialties:\n\nCES-AONL:\n- Supports AONL\'s recognition of international nurse leaders\n- Evaluates education, employment, and licensure\n- Determines eligibility for AONL certification exams\n- Fee: $150-400\n\nCES-BCEN:\n- For international nurses seeking BCEN specialty certifications\n- Validates nursing license and qualifications\n- Covers CEN®, CFRN®, CPEN®, CTRN®, TCRN®\n- Fee: $250\n\nCES-ANCC:\n- Exclusive partnership with ANCC\n- Verifies educational comparability and licensure\n- Supports four ANCC programs\n- Fee: $400'
        },
        {
          title: 'Document Processing and Timeline',
          content: 'CES document processing guidelines:\n\nIncluded in Base Service:\n- Up to 2 diploma/degree/parchment documents\n- Up to 2 license/registration documents\n- 1 identity document collection\n- Educational program information\n- Statement of educational comparability\n\nAdditional Services:\n- Additional educational credentials: $125 each\n- Additional license credentials: $125 each\n- Additional report recipients: $175\n- Expedited service: $425\n- Re-evaluation: $295\n\nProcessing Time:\n- Standard processing: 4-6 weeks\n- Expedited processing: 2-3 weeks\n- Rush processing available for additional fee'
        }
      ]
    },
    quiz: [
      {
        question: 'What is the main difference between CES Professional and Academic reports?',
        options: ['Cost', 'Professional is for licensing, Academic is for education', 'Processing time', 'Document requirements'],
        correct: 1
      },
      {
        question: 'Which CES service supports BCEN specialty certifications?',
        options: ['CES-AONL', 'CES-BCEN', 'CES-ANCC', 'CES Professional'],
        correct: 1
      },
      {
        question: 'What is the fee for expedited CES service?',
        options: ['$295', '$425', '$485', '$650'],
        correct: 1
      }
    ]
  },
  {
    id: 'visascreen-program',
    title: 'VisaScreen® Program',
    description: 'Comprehensive training on VisaScreen® visa credentials assessment service for healthcare professionals.',
    duration: 60,
    difficulty: 'Advanced',
    category: 'Verification Services',
    tags: [],
    icon: <Globe size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'VisaScreen® Overview',
          content: 'VisaScreen®: Visa Credentials Assessment Service:\n\nPurpose:\n- Comprehensive screening service for healthcare professionals\n- Required for occupational visa to work in the United States\n- Satisfies U.S. federal screening requirements\n\nKey Features:\n- Official ICHP Certificate upon successful completion\n- Approved by U.S. Department of Homeland Security (DHS)\n- Validates credentials for nine foreign healthcare professions\n- Only organization to validate seven of those professions\n\nTarget Audience:\n- Individual nurses and healthcare professionals\n- Recruiters and employers\n- Fee: $740'
        },
        {
          title: 'Eligible Healthcare Professions',
          content: 'Nine healthcare professions eligible for VisaScreen®:\n\nNursing Professions:\n- Registered Nurses (RN)\n- Licensed Practical/Vocational Nurses (LPN/LVN)\n- Nurse Practitioners\n\nAllied Health Professions:\n- Physical Therapists\n- Occupational Therapists\n- Speech-Language Pathologists\n- Medical Technologists\n- Physician Assistants\n- Clinical Laboratory Scientists\n\nRequirements:\n- Education verification\n- License verification\n- English language proficiency\n- Passing evidence of competency test'
        },
        {
          title: 'Document Requirements',
          content: 'VisaScreen® document requirements:\n\nIdentity Documents:\n- 1 identity document collection\n- 1 passport style photo\n\nEducational Credentials:\n- Unlimited diploma/degree/parchment documents\n- 1 secondary school record\n\nProfessional Credentials:\n- Unlimited license/registration documents\n- 1 English language proficiency score\n\nAdditional Requirements:\n- Passing evidence of competency test\n- Employment verification (if applicable)\n\nReport Components:\n- Verified documents\n- Educational program information\n- License/registration verification\n- English language proficiency\n- Evidence of passing qualifying exam\n- Secure digital certificate'
        },
        {
          title: 'Processing and Additional Services',
          content: 'VisaScreen® processing information:\n\nStandard Processing:\n- 4-6 weeks processing time\n- Comprehensive credential verification\n- ICHP Certificate issuance\n\nAdditional Services:\n- Expedited service: $650\n- Translation: $85/page\n- Additional educational credentials: $125 each\n- Additional license credentials: $125 each\n- Additional report recipients: $175\n- Reprocess expired order: $250\n\nRenewal Process:\n- Renewal fee: $475\n- Updated credential verification\n- New ICHP Certificate\n\nRecipients:\n- U.S. Federal Agencies\n- Regulators\n- Immigration authorities'
        }
      ]
    },
    quiz: [
      {
        question: 'How many healthcare professions does VisaScreen® validate?',
        options: ['Seven', 'Eight', 'Nine', 'Ten'],
        correct: 2
      },
      {
        question: 'What certificate do successful VisaScreen® applicants receive?',
        options: ['NYSED Certificate', 'ICHP Certificate', 'DHS Certificate', 'Federal Certificate'],
        correct: 1
      },
      {
        question: 'What is the renewal fee for VisaScreen®?',
        options: ['$425', '$475', '$650', '$740'],
        correct: 1
      }
    ]
  },
  {
    id: 'certification-programs',
    title: 'TruMerit Certification Programs',
    description: 'Training on TruMerit Certification Program®, CGN, CGN-R, and other certification offerings.',
    duration: 105,
    difficulty: 'Advanced',
    category: 'Certification Programs',
    tags: [],
    icon: <Award size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'TruMerit Certification Program®',
          content: 'TruMerit Certification Program® Overview:\n\nPurpose:\n- For first-level, general nurses educated outside the United States\n- Enables practice as Registered Nurses (RN) in the U.S.\n- Based on International Council of Nurses definition\n\nEligibility:\n- First-level, general nurses only\n- Second-level (Practical) nurses not eligible\n- Must meet educational and licensure requirements\n\nThree Required Components:\n1. Credentials evaluation assessment\n2. TruMerit Nurse Qualifying Exam (NQE)\n3. Evidence of passing approved English Language Proficiency exam\n\nTarget Audience:\n- Individual nurses\n- Recruiters and employers\n- Fee: $495'
        },
        {
          title: 'Certified Global Nurse (CGN)',
          content: 'Certified Global Nurse (CGN) Program:\n\nPurpose:\n- World\'s first truly global credential for first-level, general nurses\n- Provides greater mobility within the profession\n- Enables career advancement across geopolitical boundaries\n\nKey Features:\n- Available to any qualified nurse worldwide\n- Recognizes nursing excellence\n- Enables access to career opportunities\n- Independent of location for living and working\n\nProcess:\n- Verification of nursing education and licensure\n- Nurse Qualifying Exam (NQE)\n- Credential awarded upon passing\n\nTarget Audience:\n- Registered nurses globally\n- Executive nurses\n- Fee: $250 (Member), $350 (Non-Member)'
        },
        {
          title: 'Rehabilitation Certifications',
          content: 'Rehabilitation-focused certification programs:\n\nCertified Global Nurse – Rehabilitation (CGN-R):\n- For registered nurses in rehabilitation settings\n- Covers habilitation and restorative care\n- Earned by proctored exam\n- Fee: $450\n\nCertified Global Health Worker – Rehabilitation (CGHW-R):\n- For rehabilitation health workers\n- Validates applied knowledge and competencies\n- No post-secondary education required\n- Fee: TBD\n\nCertified Global Health Worker – Rehabilitation Advanced (CGHW-RA):\n- For rehabilitation health workers with diploma/degree\n- Advanced level certification\n- Related field education required\n- Fee: TBD\n\nRequirements:\n- At least 2 years experience (in last 5 years)\n- Passing respective rehabilitation exams\n- Ongoing professional development'
        },
        {
          title: 'Certified Global Nurse Consultant (CGNC)',
          content: 'Certified Global Nurse Consultant (CGNC) Program:\n\nPurpose:\n- Designed in collaboration with International Council of Nurses (ICN)\n- Recognizes expert nurses for knowledge and experience\n- Provides trusted nursing consultancy services\n\nEligibility Requirements:\n- Extensive nursing experience\n- Consultation experience\n- Client references\n- Professional references\n- Written self-qualification statement\n- Publications and presentations\n- Business/contingency plans\n- Awards and recognition\n- Board or committee membership\n- Grants and research studies\n- Scholarly writing and manuscript review\n- Professional associations\n\nTarget Audience:\n- Expert nurses seeking recognition\n- ICN, NNA, Government, Non-government organizations\n- Fee: $250'
        }
      ]
    },
    quiz: [
      {
        question: 'How many components are required for the TruMerit Certification Program?',
        options: ['Two', 'Three', 'Four', 'Five'],
        correct: 1
      },
      {
        question: 'What is the CGN program designed to provide?',
        options: ['U.S. licensure', 'Global mobility', 'Specialty certification', 'Academic placement'],
        correct: 1
      },
      {
        question: 'Which certification requires extensive consultation experience?',
        options: ['CGN', 'CGN-R', 'CGNC', 'CGHW-R'],
        correct: 2
      }
    ]
  },
  {
    id: 'documentation-services',
    title: 'Documentation and Verification Services',
    description: 'Training on EDAS, GP Vault, Professional License Verification, and other documentation services.',
    duration: 60,
    difficulty: 'Intermediate',
    category: 'Documentation Services',
    tags: [],
    icon: <BookOpen size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'EDAS (Electronic Document Authentication Service)',
          content: 'EDAS™ Electronic Document Service:\n\nPurpose:\n- Fully authenticates and verifies transcripts, licenses, and documents\n- Sends verified documents directly to third parties\n- Eliminates need for further verification by recipients\n\nKey Features:\n- Primary source verification\n- Direct delivery to employers, state nursing boards, schools\n- Official eDAS™ report format\n- Immediate acceptance by third parties\n\nBenefits:\n- Streamlined verification process\n- Reduced processing time\n- Enhanced document security\n- Eliminates duplicate verification requests\n\nTarget Audience:\n- All healthcare professionals\n- Employers and recruiters\n- Educational institutions\n- Regulatory bodies\n- Fee: $275'
        },
        {
          title: 'GP Vault Service',
          content: 'GPVault Online Service:\n\nPurpose:\n- Streamlines and accelerates document verification processes\n- Supports career advancement for nurses and health professionals\n- Facilitates applications for employment, licensure, and study\n\nKey Features:\n- Online document management\n- Secure document storage\n- Easy access for authorized parties\n- Supports multiple application types\n\nApplications Supported:\n- Employment applications\n- Licensure applications\n- Further study applications\n- Authorization to live and work in other countries\n\nTarget Audience:\n- Registered nurses\n- All healthcare professionals\n- Fee: $0 (included in base service)'
        },
        {
          title: 'Professional License Verification',
          content: 'Professional License Verification Service:\n\nPurpose:\n- Verifies licenses in a profession for specific recipients\n- Supports regulatory compliance\n- Ensures current and valid licensure status\n\nProcess:\n- Direct verification with licensing authority\n- Current status confirmation\n- Detailed license information\n- Secure verification report\n\nTarget Audience:\n- Registered nurses\n- Employers\n- State Boards of Nursing (SBoN)\n- ANCC and other certification organizations\n- VA and other government agencies\n\nBenefits:\n- Real-time verification\n- Reduced fraud risk\n- Streamlined hiring process\n- Regulatory compliance assurance\n- Fee: $195'
        },
        {
          title: 'Additional Documentation Services',
          content: 'Supporting documentation services:\n\nDocument Translation:\n- Translate required documents from foreign language to English\n- Standard 8.5 x 11 inch documents\n- Professional translation services\n- Fee: $85 per page\n\nForwarding English Test Results:\n- Forward English language test scores to selected licensing boards\n- Meet program requirements\n- Streamlined score delivery\n- Fee: $50\n\nTruMerit Self-Assessment:\n- Web-based preparation tool for TruMerit Qualifying Exam®\n- Real questions from previous exams\n- Customizable learning styles\n- Practice under exam conditions or self-paced\n- Fee: $50'
        }
      ]
    },
    quiz: [
      {
        question: 'What is the main benefit of EDAS service?',
        options: ['Lower cost', 'Eliminates duplicate verification', 'Faster processing', 'Better security'],
        correct: 1
      },
      {
        question: 'What is the fee for GP Vault service?',
        options: ['$50', '$195', '$275', '$0'],
        correct: 3
      },
      {
        question: 'What does the Self-Assessment tool help prepare for?',
        options: ['NCLEX', 'TruMerit Qualifying Exam', 'English test', 'Interview'],
        correct: 1
      }
    ]
  },
  {
    id: 'specialty-certifications',
    title: 'Specialty Certification Services',
    description: 'Training on specialty certification services including AONL, BCEN, ANCC, and ISPN programs.',
    duration: 90,
    difficulty: 'Advanced',
    category: 'Certification Programs',
    tags: [],
    icon: <Star size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'AONL Certification Support',
          content: 'CES-AONL Service:\n\nPurpose:\n- Supports AONL\'s recognition of high-quality nurse leaders\n- Evaluates international nurse management expertise\n- Determines eligibility for AONL certification examinations\n\nEvaluation Process:\n- Reviews education documents\n- Validates employment experience\n- Verifies license/registration status\n- Assesses management knowledge and expertise\n\nTarget Audience:\n- International nurse leaders\n- Nurse executives and managers\n- Healthcare administrators\n\nBenefits:\n- Recognition of international expertise\n- Pathway to AONL certification\n- Professional development support\n- Global leadership recognition\n- Fee: $150-400'
        },
        {
          title: 'BCEN Specialty Certifications',
          content: 'CES-BCEN Service:\n\nPurpose:\n- Evaluates credentials of international nurses seeking BCEN specialty certifications\n- Validates nursing license and qualifications\n- Determines eligibility for BCEN certification applications\n\nSupported Certifications:\n- Certified Emergency Nurse (CEN®)\n- Certified Flight Registered Nurse (CFRN®)\n- Certified Pediatric Emergency Nurse (CPEN®)\n- Certified Transport Registered Nurse (CTRN®)\n- Trauma Certified Registered Nurse (TCRN®)\n\nProcess:\n- Validates nursing license\n- Assesses academic and professional qualifications\n- Evaluates against BCEN standards\n- Determines eligibility for certification application\n\nImportant Note:\n- TruMerit does not issue specialty certifications\n- Eligible applicants apply directly to BCEN\n- Fee: $250'
        },
        {
          title: 'ANCC Partnership Program',
          content: 'CES-ANCC Service:\n\nPurpose:\n- Exclusive partnership with American Nurses Credentialing Center (ANCC)\n- Verifies educational comparability and licensure\n- Supports four ANCC programs\n\nPartnership Benefits:\n- Direct collaboration with ANCC\n- Streamlined evaluation process\n- Recognized expertise in international credential evaluation\n- Comprehensive assessment standards\n\nSupported Programs:\n- Four specific ANCC certification programs\n- International nurse credential recognition\n- Educational comparability assessment\n- Licensure verification\n\nTarget Audience:\n- International nurses seeking ANCC certification\n- Healthcare organizations\n- Educational institutions\n- Fee: $400'
        },
        {
          title: 'ISPN Program',
          content: 'ISPN® (International Standards for Professional Nurses) Program:\n\nPurpose:\n- Measures nursing knowledge against international standards\n- Validates nursing education for international standards\n- Supports nursing practice in specific countries\n\nProgram Components:\n1. Initial verification of first-level, general nurse status\n2. Passing the TruMerit Qualifying Exam®\n\nEligibility:\n- Foreign-educated nurses\n- Must reside in program country\n- Currently available in China and Indonesia\n\nTarget Audience:\n- Registered nurses in China and Indonesia\n- ISPN-ITC and ISPN-China Health organizations\n- Fee: $395'
        }
      ]
    },
    quiz: [
      {
        question: 'Which organization does TruMerit have an exclusive partnership with?',
        options: ['AONL', 'BCEN', 'ANCC', 'ISPN'],
        correct: 2
      },
      {
        question: 'How many BCEN specialty certifications does CES-BCEN support?',
        options: ['Three', 'Four', 'Five', 'Six'],
        correct: 2
      },
      {
        question: 'In which countries is the ISPN program currently available?',
        options: ['China and Japan', 'China and Indonesia', 'Indonesia and Philippines', 'China and Korea'],
        correct: 1
      }
    ]
  },
  {
    id: 'customer-service-support',
    title: 'Customer Service and Support',
    description: 'Training on customer service best practices, fee structures, and support processes for TruMerit services.',
    duration: 75,
    difficulty: 'Beginner',
    category: 'Customer Service',
    tags: [],
    icon: <Users size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'Service Fee Structure',
          content: 'Understanding TruMerit service fees:\n\nCore Services:\n- CVS New York: $425\n- CVS NCNZ: $380\n- CVS Aruba: $395\n- CES Professional: $485\n- CES Academic: $485\n- VisaScreen: $740\n- NNAS: $595 (USD to CAD conversion)\n- Certification Program: $495\n\nSpecialty Services:\n- CGN-R: $450\n- CGHW-R: TBD\n- CGHW-RA: TBD\n- EDAS: $275\n- GP Vault: $0\n- Professional License Verification: $195\n\nAdditional Services:\n- Translation: $85/page\n- Expedited service: $425-650\n- Additional credentials: $50-125 each\n- Additional report recipients: $175\n- Re-evaluation: $250-295'
        },
        {
          title: 'Customer Communication',
          content: 'Effective customer communication strategies:\n\nInitial Contact:\n- Understand customer needs and goals\n- Explain appropriate service options\n- Provide clear fee structure information\n- Set realistic expectations for processing time\n\nDocumentation Guidance:\n- Explain required documents clearly\n- Provide document preparation tips\n- Address common documentation issues\n- Offer translation services when needed\n\nStatus Updates:\n- Regular communication during processing\n- Proactive updates on delays or issues\n- Clear explanation of next steps\n- Timely completion notifications\n\nPost-Service Support:\n- Explain report contents and usage\n- Provide guidance on next steps\n- Offer additional services as needed\n- Maintain professional relationship'
        },
        {
          title: 'Common Customer Issues',
          content: 'Addressing frequent customer concerns:\n\nDocumentation Issues:\n- Missing or incomplete documents\n- Translation requirements\n- Document authentication needs\n- Expired documents\n\nProcessing Delays:\n- High volume periods\n- Complex verification requirements\n- International communication delays\n- Additional information requests\n\nFee and Payment:\n- Understanding fee structures\n- Payment method options\n- Refund policies\n- Additional service costs\n\nService Selection:\n- Choosing appropriate service\n- Understanding service differences\n- Multiple service needs\n- Recipient requirements\n\nResolution Strategies:\n- Clear communication\n- Proactive problem-solving\n- Escalation procedures\n- Customer satisfaction focus'
        },
        {
          title: 'Quality Assurance',
          content: 'Maintaining service quality standards:\n\nAccuracy Standards:\n- Thorough document verification\n- Primary source confirmation\n- Cross-reference validation\n- Quality control checks\n\nTimeline Management:\n- Standard processing times\n- Expedited service delivery\n- Proactive delay communication\n- Deadline management\n\nCustomer Satisfaction:\n- Regular feedback collection\n- Service improvement implementation\n- Training and development\n- Performance monitoring\n\nCompliance:\n- Regulatory requirement adherence\n- Security protocol maintenance\n- Data protection standards\n- Professional ethics\n\nContinuous Improvement:\n- Process optimization\n- Technology enhancement\n- Staff development\n- Service expansion'
        }
      ]
    },
    quiz: [
      {
        question: 'What is the fee for translation services?',
        options: ['$50 per page', '$75 per page', '$85 per page', '$100 per page'],
        correct: 2
      },
      {
        question: 'What is the most important aspect of customer communication?',
        options: ['Speed', 'Accuracy', 'Understanding customer needs', 'Cost efficiency'],
        correct: 2
      },
      {
        question: 'What should be done when processing delays occur?',
        options: ['Wait for customer to ask', 'Provide proactive updates', 'Reduce fees', 'Skip quality checks'],
        correct: 1
      }
    ]
  },
  {
    id: 'regulatory-compliance',
    title: 'Regulatory Compliance and Standards',
    description: 'Training on regulatory requirements, compliance standards, and international healthcare regulations.',
    duration: 90,
    difficulty: 'Advanced',
    category: 'Regulatory Compliance',
    tags: [],
    icon: <CheckCircle size={32} color="#0477aa" />,
    assignments: {
      roles: ['employee'],
      users: [],
      departments: []
    },
    content: {
      sections: [
        {
          title: 'U.S. Regulatory Framework',
          content: 'Understanding U.S. healthcare regulatory requirements:\n\nDepartment of Homeland Security (DHS):\n- TruMerit approved for nine healthcare professions\n- VisaScreen® satisfies federal screening requirements\n- Occupational visa requirements\n- Immigration compliance standards\n\nState Licensing Boards:\n- New York State Education Department (NYSED)\n- Individual state nursing board requirements\n- License verification standards\n- Continuing education requirements\n\nFederal Agencies:\n- U.S. Citizenship and Immigration Services (USCIS)\n- Department of Labor requirements\n- Healthcare workforce regulations\n- Professional practice standards\n\nCompliance Requirements:\n- Document authentication standards\n- Primary source verification\n- Security protocol adherence\n- Data protection requirements'
        },
        {
          title: 'International Regulatory Standards',
          content: 'International healthcare regulatory frameworks:\n\nNew Zealand:\n- Nursing Council of New Zealand (NCNZ) requirements\n- International nurse registration standards\n- Credential verification protocols\n- Professional practice guidelines\n\nAruba:\n- Department of Public Health Aruba standards\n- Healthcare professional registration\n- International credential recognition\n- Practice authorization requirements\n\nCanada:\n- NNAS (National Nursing Assessment Service) standards\n- Provincial nursing board requirements\n- International nurse assessment\n- Professional competency evaluation\n\nGlobal Standards:\n- International Council of Nurses (ICN) guidelines\n- World Health Organization (WHO) standards\n- Cross-border healthcare regulations\n- Professional mobility frameworks'
        },
        {
          title: 'Documentation Standards',
          content: 'International documentation and verification standards:\n\nPrimary Source Verification:\n- Direct verification from issuing institutions\n- Authentication of document authenticity\n- Cross-reference validation\n- Security protocol implementation\n\nDocument Authentication:\n- Notarization requirements\n- Apostille certification\n- Embassy verification\n- Translation standards\n\nEducational Credential Assessment:\n- Course-by-course evaluation\n- Grade point average conversion\n- Program comparability analysis\n- Academic equivalency determination\n\nProfessional Credential Verification:\n- License status confirmation\n- Practice authorization validation\n- Continuing education verification\n- Professional competency assessment'
        },
        {
          title: 'Quality Assurance and Auditing',
          content: 'Quality assurance and compliance auditing:\n\nInternal Quality Controls:\n- Regular process audits\n- Document verification accuracy checks\n- Timeline compliance monitoring\n- Customer satisfaction surveys\n\nExternal Compliance:\n- Regulatory body audits\n- Third-party quality assessments\n- International standard compliance\n- Professional association reviews\n\nRisk Management:\n- Fraud prevention measures\n- Security breach protocols\n- Data protection compliance\n- Professional liability management\n\nContinuous Improvement:\n- Process optimization\n- Technology enhancement\n- Staff training and development\n- Service quality enhancement\n\nReporting and Documentation:\n- Compliance reporting\n- Quality metrics tracking\n- Audit trail maintenance\n- Performance documentation'
        }
      ]
    },
    quiz: [
      {
        question: 'How many healthcare professions is TruMerit approved to validate for DHS?',
        options: ['Seven', 'Eight', 'Nine', 'Ten'],
        correct: 2
      },
      {
        question: 'What is the primary purpose of primary source verification?',
        options: ['Reduce costs', 'Ensure document authenticity', 'Speed processing', 'Simplify procedures'],
        correct: 1
      },
      {
        question: 'What is essential for continuous improvement in quality assurance?',
        options: ['Reducing staff', 'Process optimization', 'Increasing fees', 'Limiting services'],
        correct: 1
      }
    ]
  }
];